import * as React from "react";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import ExpandToolBoxButton from "./ExpandToolBoxButton";
import { Grid, Stack, Typography, useTheme } from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";
import {
  NumberCommaFormatter,
  dynamicPriceFormatter,
  formatCurrency,
  numberFormatter,
} from "src/utils/helpers/PriceAndUnitsHelpers";
import { GainComponent } from "./CoinHeader";
import { useMarketsState } from "src/store/features/markets.slice";
import { trackEvent } from "src/Amplitude";

type Anchor = "top" | "left" | "bottom" | "right";

export default function ToolBox() {
  const {
    coinStatistics: { marketCap, tradingVolume, rank, supply, supplyRatio, change = 0 },
    candleChartState = { high: 0, low: 0 },
  } = useMarketsState();

  const variant = change < 0 ? "lose" : "gain";

  const theme = useTheme();
  const toolBoxContainer = {
    padding: "32px 28px",
    marginTop: { xs: "110px", sm: "110px", md: "60px" },
  };

  const [state, setState] = React.useState({
    right: false,
  });

  const toggleDrawer =
    (anchor: Anchor, open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      trackEvent("ToolBox_Open", { open }, "Coin_Page");
      setState({ ...state, [anchor]: open });
    };

  const titles = [
    "Market cap",
    "Market Cap Rank",
    "24 Hour Volume",
    "Circulating Supply",
    "Circulating Supply Ratio",
    "24H High",
    "24H Low",
  ];

  const formattedMarketCap = formatCurrency(marketCap ?? 0, "USD", {
    type: "name",
  });
  const formattedTradingVolume = formatCurrency(tradingVolume ?? 0, "USD", {
    type: "name",
  });

  const data = [
    formattedMarketCap,
    rank,
    formattedTradingVolume,
    NumberCommaFormatter(supply),
    supplyRatio ? `${supplyRatio.toString().slice(0, 5)}%` : "Infinite Supply",
    dynamicPriceFormatter(candleChartState?.high || 0),
    dynamicPriceFormatter(candleChartState?.low || 0),
  ];

  return (
    <div>
      <React.Fragment key={"right"}>
        <Button disableRipple sx={{ padding: "0px" }} onClick={toggleDrawer("right", true)}>
          <ExpandToolBoxButton />
        </Button>
        <Drawer
          PaperProps={{
            sx: {
              width: { md: "380px", sm: "100%", xs: "100%" },
              zIndex: theme.zIndex.appBar - 1,
            },
          }}
          hideBackdrop
          anchor={"right"}
          open={state["right"]}
          onClose={toggleDrawer("right", false)}>
          {/* Drawer Content */}
          <Grid container sx={toolBoxContainer}>
            <Grid item xs={12} display="flex" justifyContent="end" mb={1} margin="unset">
              <CloseOutlined onClick={() => setState({ ...state, right: false })} />
            </Grid>
            <Grid item xs={12} sx={{ marginBottom: "20px" }}>
              <Typography sx={{ fontSize: "16px", fontWeight: "600" }}>Market Data</Typography>
            </Grid>
            <Grid item xs={12}>
              {titles.map((title, index) => {
                const isGainComponent = index === 0;
                return (
                  <Stack
                    marginBottom={"16px"}
                    direction="row"
                    justifyContent={"space-between"}
                    key={index}>
                    <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>{title}</Typography>
                    <Stack direction="row">
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "600",
                          marginRight: isGainComponent ? "5px" : "0px",
                        }}>
                        {data[index]}
                      </Typography>
                      {isGainComponent && (
                        <GainComponent value={numberFormatter(change)} variant={variant} />
                      )}
                    </Stack>
                  </Stack>
                );
              })}
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "12px", fontWeight: "400" }}>
                Indicative prices only
              </Typography>
            </Grid>
          </Grid>
        </Drawer>
      </React.Fragment>
    </div>
  );
}
